import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import branding from '../../branding.json';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiLock, FiLogIn } from 'react-icons/fi';

export const LoginModal: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { darkMode } = useTheme();
  
  // Get auth context values
  const { 
    isLoginModalOpen, 
    closeLoginModal, 
    login, 
    loginWithProvider, 
    authConfig, 
    error: authError,
    loading: authLoading
  } = useAuth();
  
  // Local UI error state
  const [uiError, setUiError] = useState<string | null>(null);
  
  // Update UI error when auth error changes
  useEffect(() => {
    setUiError(authError);
  }, [authError]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUiError(null);
    
    try {
      await login({ username, password });
      closeLoginModal();
    } catch (err: any) {
      setUiError(err.response?.data?.detail || 'Failed to login');
    }
  };
  
  const handleProviderLogin = (provider: string) => {
    try {
      loginWithProvider(provider);
      // Note: We don't close the modal here as the provider will redirect
    } catch (err) {
      console.error('Error redirecting to provider login:', err);
      setUiError('Failed to redirect to login provider');
    }
  };
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeLoginModal();
      }
    };
    
    if (isLoginModalOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isLoginModalOpen, closeLoginModal]);
  
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isLoginModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLoginModalOpen]);
  
  // Get primary color from branding
  const primaryColor = branding.theme.primaryColor || '#3B82F6';
  
  // Add a class to the body when modal is open to prevent scrolling and add blur
  useEffect(() => {
    if (isLoginModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isLoginModalOpen]);

  return (
    <AnimatePresence>
      {isLoginModalOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={closeLoginModal}
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} p-8 rounded-lg shadow-xl w-full max-w-md mx-4 animate-scaleIn relative z-10`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Subtle decorative elements */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-500/10 to-purple-600/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-400/10 to-indigo-500/10 rounded-full blur-3xl"></div>
              </div>
              
              {/* Close button */}
              <button
                onClick={closeLoginModal}
                className={`absolute top-4 right-4 p-1 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {/* Logo */}
              {branding.logo.enabled && (
                <div className="flex justify-center mb-6">
                  <img 
                    src={branding.logo.url} 
                    alt={branding.logo.alt || branding.appName} 
                    className="h-16 object-contain"
                  />
                </div>
              )}
              
              <h1 className="text-2xl font-bold mb-2 text-center">{branding.appName}</h1>
              {branding.header.tagline && (
                <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                  {branding.header.tagline}
                </p>
              )}
              
              {uiError && (
                <div className={`mb-4 p-3 ${darkMode ? 'bg-red-900 text-red-100' : 'bg-red-100 text-red-700'} rounded-md flex items-start`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>{uiError}</span>
                </div>
              )}
              
              {/* OIDC Provider Buttons */}
              {authConfig?.oidc_enabled && authConfig.providers && authConfig.providers.length > 0 && (
                <div className="space-y-3 mb-6">
                  {authConfig.providers.map((provider: string) => (
                    provider !== 'local' && (
                      <button
                        key={provider}
                        onClick={() => handleProviderLogin(provider)}
                        className={`w-full flex items-center justify-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium
                          ${darkMode 
                            ? 'border-gray-600 text-gray-200 bg-gray-700 hover:bg-gray-600' 
                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                        disabled={authLoading}
                      >
                        {/* Provider icon */}
                        <span className="mr-2 text-blue-500">
                          {provider === 'google' ? (
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                              <path
                                fill="currentColor"
                                d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
                              />
                            </svg>
                          ) : provider === 'keycloak' ? (
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                              <path
                                fill="currentColor"
                                d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"
                              />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                              <path
                                fill="currentColor"
                                d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"
                              />
                            </svg>
                          )}
                        </span>
                        Continue with {provider.charAt(0).toUpperCase() + provider.slice(1)}
                      </button>
                    )
                  ))}
                </div>
              )}
              
              {/* Divider if both auth methods are enabled */}
              {authConfig?.local_auth_enabled && authConfig?.oidc_enabled && authConfig.providers.some(p => p !== 'local') && (
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className={`w-full border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className={`px-2 ${darkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>
                      Or continue with
                    </span>
                  </div>
                </div>
              )}
              
              {/* Local Auth Form */}
              {authConfig?.local_auth_enabled && (
                <form onSubmit={handleSubmit} className={`${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} p-5 rounded-lg border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <div className="mb-4">
                    <label className={`block ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-2 flex items-center text-sm font-medium`}>
                      <FiUser className="mr-2" />
                      Username
                    </label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50
                        ${darkMode 
                          ? 'bg-gray-800 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoComplete="username"
                      placeholder="Enter your username"
                    />
                  </div>
                  
                  <div className="mb-6">
                    <label className={`block ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-2 flex items-center text-sm font-medium`}>
                      <FiLock className="mr-2" />
                      Password
                    </label>
                    <input
                      type="password"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50
                        ${darkMode 
                          ? 'bg-gray-800 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      placeholder="Enter your password"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    style={{ 
                      backgroundColor: authLoading ? undefined : primaryColor,
                    }}
                    className={`w-full text-white py-2 rounded-md hover:opacity-90 transition-opacity flex items-center justify-center
                      ${authLoading ? (darkMode ? 'bg-gray-600' : 'bg-gray-400') : ''}`}
                    disabled={authLoading}
                  >
                    {authLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Logging in...
                      </>
                    ) : (
                      <>
                        <FiLogIn className="mr-2" />
                        Login
                      </>
                    )}
                  </button>
                </form>
              )}
              
              {/* Footer with copyright */}
              <div className={`mt-8 text-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {branding.copyright}
              </div>
              
              {/* Company info */}
              {branding.companyInfo && (
                <div className={`mt-2 text-center text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <a 
                    href={branding.companyInfo.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {branding.companyInfo.name}
                  </a>
                </div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
